import { useMemo, useState, useRef, useCallback } from 'react';
import type { SortingState, VisibilityState, RowSelectionState, Table } from '@tanstack/react-table';
import type { AnalyticalRow, AnalyticalResponse, AnomaliesResponse, ConcentrationUnit, NdHandling } from '@/api/dataVault';
import { SkeletonTable } from '../components/ui/SkeletonLoader';
import { DataTable, DataTableColumnVisibility, type DataTableRef } from '../components/table';
import { exportToCSV, exportToXLSX } from '../components/table/tableExport';
import { CustomExportDialog } from '../components/table/CustomExportDialog';
import { createAnalyticalColumns } from './analyticalColumns';

interface Props {
  data: AnalyticalResponse | undefined;
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSortChange: (col: string) => void;
  unit: ConcentrationUnit;
  ndHandling: NdHandling;
  anomalies?: AnomaliesResponse;
  showAnomalies: boolean;
  onSelectWell?: (wellId: string) => void;
}

export function AnalyticalTab({
  data, isLoading, page, onPageChange, sortBy, sortDir, onSortChange,
  unit, ndHandling, anomalies, showAnomalies,
  onSelectWell,
}: Props) {
  const tableRef = useRef<DataTableRef<AnalyticalRow>>(null);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [customExportOpen, setCustomExportOpen] = useState(false);
  // Track the table instance for the export dialog (set via toolbar render prop)
  const [tableInstance, setTableInstance] = useState<Table<AnalyticalRow> | null>(null);

  // Build anomaly lookup map
  const anomalyMap = useMemo(() => {
    const map = new Map<string, { z_score: number; direction: string }>();
    if (showAnomalies && anomalies) {
      for (const a of anomalies.anomalies) {
        map.set(a.result_id, { z_score: a.z_score, direction: a.direction });
      }
    }
    return map;
  }, [showAnomalies, anomalies]);

  // Column definitions
  const columns = useMemo(
    () => createAnalyticalColumns({ unit, ndHandling, anomalyMap, showAnomalies, onSelectWell }),
    [unit, ndHandling, anomalyMap, showAnomalies, onSelectWell],
  );

  // Bridge: parent uses sortBy/sortDir strings, DataTable uses TanStack SortingState
  const sorting: SortingState = useMemo(
    () => sortBy ? [{ id: sortBy, desc: sortDir === 'desc' }] : [],
    [sortBy, sortDir],
  );

  const handleSortingChange = useCallback((next: SortingState) => {
    if (next.length > 0) {
      onSortChange(next[0].id);
    }
  }, [onSortChange]);

  // Bridge: parent uses 1-indexed page, DataTable uses 0-indexed pageIndex
  const handlePaginationChange = useCallback((zeroIdx: number) => {
    onPageChange(zeroIdx + 1);
  }, [onPageChange]);

  // Toolbar rendered inside DataTable — has direct access to table instance
  const renderToolbar = useCallback((table: Table<AnalyticalRow>) => {
    // Capture the table instance for the CustomExportDialog
    if (!tableInstance) setTableInstance(table);
    return null;
  }, [tableInstance]);

  if (isLoading && !data) {
    return (
      <div className="animate-fade-in">
        <SkeletonTable rows={10} />
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-mono-400 text-xs tracking-wider">
          {data?.total === 0 ? 'NO RESULTS MATCH YOUR FILTERS' : 'SELECT SITES TO VIEW DATA'}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3" style={{ borderBottom: '1px solid transparent', borderImage: 'linear-gradient(to right, rgba(6,182,212,0.3), rgba(168,85,247,0.3)) 1' }}>
        <div className="text-xs text-mono-200 tracking-wider pb-2 truncate max-w-[200px] sm:max-w-none">
          {data.total.toLocaleString()} RESULTS
          {data.nd_handling !== 'exclude' && (
            <span className="ml-2 text-mono-300">
              ND: {
                data.nd_handling === 'half_mdl' ? '\u00BD MDL' :
                data.nd_handling === 'less_than' ? '<DL' :
                data.nd_handling.toUpperCase()

              }
            </span>
          )}
          {showAnomalies && anomalies && (
            <span className="ml-2 text-amber-500">
              {anomalies.total_flagged} ANOMALIES ({anomalies.threshold}\u03C3)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 pb-2">
          <button
            onClick={() => tableInstance && exportToCSV(tableInstance, 'analytical-data')}
            className="px-2.5 py-1 text-[10px] tracking-wider text-mono-300 hover:text-mono-100 bg-mono-800 rounded transition-colors"
          >
            CSV
          </button>
          <button
            onClick={() => tableInstance && exportToXLSX(tableInstance, 'analytical-data')}
            className="px-2.5 py-1 text-[10px] tracking-wider text-mono-300 hover:text-mono-100 bg-mono-800 rounded transition-colors"
          >
            XLSX
          </button>
          <button
            onClick={() => setCustomExportOpen(true)}
            className="px-2.5 py-1 text-[10px] tracking-wider text-mono-300 hover:text-mono-100 bg-mono-800 rounded transition-colors"
          >
            CUSTOM
          </button>
          {tableInstance && (
            <DataTableColumnVisibility
              table={tableInstance}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          )}
        </div>
      </div>

      {/* TanStack Table */}
      <DataTable<AnalyticalRow>
        ref={tableRef}
        columns={columns}
        data={data.items}
        pageCount={data.total_pages}
        pageIndex={page - 1}
        pageSize={data.page_size}
        onPaginationChange={handlePaginationChange}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        rowClassName={(row) => anomalyMap.has(row.original.id) ? 'bg-amber-900/5' : ''}
        enableVirtualization
        toolbar={renderToolbar}
      />

      {/* Custom Export Dialog */}
      {tableInstance && (
        <CustomExportDialog
          open={customExportOpen}
          onClose={() => setCustomExportOpen(false)}
          table={tableInstance}
          filename="analytical-data"
        />
      )}
    </div>
  );
}
